import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Asterisk } from "lucide-react";
import RevealText from "@/components/RevealText";
import { useLang } from "@/i18n";

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const blockY = useTransform(scrollYProgress, [0, 1], [0, 300]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden pt-28 pb-14 md:pb-20 border-b border-[#27272A]"
    >
      {/* Signal-blue parallax accent block with rotating badge */}
      <motion.div
        style={{ y: blockY }}
        aria-hidden="true"
        className="pointer-events-none absolute top-28 right-6 md:right-16 w-[38vw] md:w-[24vw] aspect-square bg-brand flex items-center justify-center p-4 md:p-8"
      >
        <motion.div
          className="relative w-full h-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 18, ease: "linear", repeat: Infinity }}
        >
          <svg viewBox="0 0 200 200" className="w-full h-full">
            <defs>
              <path
                id="badge-curve"
                d="M 100,100 m -74,0 a 74,74 0 1,1 148,0 a 74,74 0 1,1 -148,0"
              />
            </defs>
            <text
              fill="#FAFAFA"
              style={{ fontFamily: "Satoshi, sans-serif", fontWeight: 700, letterSpacing: "6px" }}
              fontSize="15"
            >
              <textPath href="#badge-curve" startOffset="0">
                AVAILABLE FOR WORK · 2026 · OPEN TO PROJECTS ·
              </textPath>
            </text>
          </svg>
        </motion.div>
        {/* Spinning asterisk in the centre */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          animate={{ rotate: -360 }}
          transition={{ duration: 8, ease: "linear", repeat: Infinity }}
        >
          <Asterisk className="text-white w-[22%] h-[22%]" strokeWidth={2.5} />
        </motion.div>
      </motion.div>


      <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex items-center gap-3 mb-8 font-satoshi font-bold text-xs md:text-sm tracking-[0.3em] uppercase"
        >
          <span className="w-4 h-4 bg-brand" />
          {t.hero.badge}
        </motion.div>

        <motion.h1
          style={{ y: textY }}
          className="font-cabinet font-black uppercase tracking-tighter leading-[0.8] text-[17vw] md:text-[14vw]"
        >
          <RevealText lines={["Jaxson"]} delay={0.3} className="block" />
          <RevealText lines={["Dinmore"]} delay={0.45} className="block text-outline" />
        </motion.h1>

        {/* Red divider */}
        <div className="mt-10 mb-8 h-1.5 w-full bg-brand" />

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="font-satoshi font-medium text-xl md:text-2xl text-[#d4d4d8] max-w-xl leading-snug"
          >
            {t.hero.tagline}
            <span className="text-accent font-bold"> {t.hero.taglineAccent}</span>
          </motion.p>

          <motion.a
            href="#work"
            data-testid="hero-scroll-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            onClick={(e) => { e.preventDefault(); document.querySelector("#work")?.scrollIntoView({ behavior: "smooth" }); }}
            className="group inline-flex items-center gap-4 font-satoshi font-bold text-sm uppercase tracking-widest bg-white text-[#09090B] px-6 py-4 self-start hover:bg-brand hover:text-white transition-colors duration-200"
          >
            {t.hero.cta}
            <ArrowDownRight size={20} className="group-hover:translate-x-1 group-hover:translate-y-1 transition-transform" />
          </motion.a>
        </div>
      </div>
    </section>
  );
}
